import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { concatParams, CreateParamsJson } from "../../helper/pathHelper";
import {
  extendActive,
  finishActive,
  pause,
  resume,
} from "../../helper/liveControls";
import { formatCountdown, formatTime } from "../../helper/timeCalculations";
import { CARD_TYPES } from "../../helper/cardTypes";
import { useLanguage } from "../../i18n/LanguageContext";

// The small always-on-top timer (see usePictureInPicture): the running
// card, its countdown and what comes next, plus the live controls that
// matter mid-session. It is part of the timer widget's React tree, so it
// reads the same plan from the URL and its buttons edit the main window.
export default function MiniTimer({
  pipWindow,
  starttime,
  agenda,
  now,
  activeCard,
  isEmpty,
  isOver,
  isPaused,
  warningLeadSeconds,
}) {
  const json = CreateParamsJson();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const apply = (change) =>
    navigate({
      pathname: location.pathname,
      search: concatParams(change(json)),
    });

  const task = activeCard ? agenda[activeCard.index] : null;
  const next = activeCard ? agenda[activeCard.index + 1] : agenda[0];
  const notStarted = !isEmpty && !activeCard && !isOver && now.isBefore(starttime);

  let topic = t("common.noCardsYet");
  let countdown = "–:––";
  let share = 0;
  if (task) {
    topic = task.topic || t("common.untitled");
    countdown = formatCountdown(activeCard.remainingSeconds);
    share = 1 - activeCard.remainingSeconds / activeCard.durationSeconds;
  } else if (isOver) {
    topic = t("common.done");
    countdown = "0:00";
    share = 1;
  } else if (notStarted) {
    topic = t("mini.startsAt", { time: formatTime(starttime) });
    countdown = formatCountdown(starttime.diff(now, "seconds", true));
  }
  const isWarning =
    Boolean(task) && activeCard.remainingSeconds <= warningLeadSeconds;

  return createPortal(
    <div
      className={`mini-timer${isWarning ? " is-warning" : ""}${
        isPaused ? " is-paused" : ""
      }`}
      style={{
        "--mini-type": CARD_TYPES[task?.type]?.color,
      }}
    >
      <div className="mini-timer-topic" title={topic}>
        {topic}
      </div>
      <div className="mini-timer-countdown" role="timer">
        {countdown}
        {isPaused && <span className="mini-timer-paused">{t("live.paused")}</span>}
      </div>
      <div className="mini-timer-bar" aria-hidden="true">
        <span style={{ width: `${Math.min(1, Math.max(0, share)) * 100}%` }} />
      </div>
      {!isOver && next && (activeCard || notStarted) && (
        <div className="mini-timer-next">
          {activeCard ? t("mini.next") : t("mini.first")}{" "}
          <strong>{next.topic || t("common.untitled")}</strong>
        </div>
      )}
      {(activeCard || isPaused) && (
        <div className="mini-timer-controls">
          <button
            type="button"
            onClick={() => apply(isPaused ? resume : pause)}
          >
            {isPaused ? t("live.resume") : t("live.pause")}
          </button>
          <button
            type="button"
            aria-label={t("live.plusOne")}
            title={t("live.plusOne")}
            disabled={!activeCard}
            onClick={() => apply((params) => extendActive(params, 60))}
          >
            +1
          </button>
          <button
            type="button"
            aria-label={t("live.nextLabel")}
            title={t("live.nextLabel")}
            disabled={!activeCard}
            onClick={() => apply(finishActive)}
          >
            {t("live.next")} ›
          </button>
        </div>
      )}
    </div>,
    pipWindow.document.body
  );
}
